import { memo } from 'react';

import { Row } from '@tanstack/react-table';
import { AlertTriangle } from 'lucide-react';

import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import i18n from '@/core/i18n';

import { EmptyDescriptionScope } from '../../../components/modals/empty-description-info-modal';
import { ApprovalProjectRow } from '../../../types/time-tracking-approvals';
import { asTimeEntry, asTimeOffEntry, isTimeEntryRow, isTimeOffRow } from './approval-row.helpers';

interface ApprovalDescriptionCellProps {
  row: Row<ApprovalProjectRow>;
  /** Stable, scoped handler from the column (avoids a per-render closure that breaks memo). */
  onShowEmptyDescriptionInfo: (scope: EmptyDescriptionScope) => void;
}

/**
 * Time-entry description cell. Shows the (truncated, tooltip-backed) description,
 * or a warning affordance opening the explanatory modal when it's empty.
 * Time-off rows show the time-off type name plus the optional description/reason.
 * Aggregate rows render an em dash.
 */
function ApprovalDescriptionCellComponent({
  row,
  onShowEmptyDescriptionInfo,
}: ApprovalDescriptionCellProps) {
  if (isTimeOffRow(row)) {
    const timeOff = asTimeOffEntry(row);
    const text = timeOff.description
      ? `${timeOff.timeOffTypeName} — ${timeOff.description}`
      : timeOff.timeOffTypeName;

    return <TruncatedTooltip content={text} className="w-full truncate text-sm font-medium" />;
  }

  if (!isTimeEntryRow(row)) {
    // eslint-disable-next-line i18next/no-literal-string
    return <span className="text-muted-foreground">—</span>;
  }

  const entry = asTimeEntry(row);
  const hasDescription = !!entry.description && entry.description.trim().length > 0;

  if (!hasDescription) {
    return (
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onShowEmptyDescriptionInfo('entry');
        }}
        className="flex w-full items-center gap-1.5 text-left text-sm text-warning hover:opacity-80"
        aria-label={i18n.t('authenticated.timeTracking:approvals.emptyDescription.iconAriaLabel')}
      >
        <AlertTriangle size={14} strokeWidth={2} className="shrink-0" />
        <span className="truncate">
          {i18n.t('authenticated.timeTracking:approvals.emptyDescription.label')}
        </span>
      </button>
    );
  }

  return (
    <TruncatedTooltip
      content={entry.description}
      preserveWhitespace
      className="w-full truncate text-sm"
    />
  );
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every description cell. */
export const ApprovalDescriptionCell = memo(ApprovalDescriptionCellComponent);
